import React, { useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";
import { StyledButtonMain } from "../common/Buttons";

// ─── Brand colour helper ──────────────────────────────────────────────────────
const BRAND =
  process.env.REACT_APP_API_DOMAIN !== "https://www.thrive-drive.org:8443"
    ? "rgb(63, 93, 125)"
    : "rgb(158, 31, 99)";

// ─── Keyword highlight ────────────────────────────────────────────────────────
function highlightMatch(text: unknown, term: string): React.ReactNode {
  const str = text == null ? "" : String(text);
  if (!term || !str) return str;
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = str.split(new RegExp(`(${escaped})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === term.toLowerCase() ? (
      <mark
        key={i}
        className="bg-yellow-200 rounded px-0.5"
        style={{
          backgroundColor: "#fef08a",
          borderRadius: 2,
          padding: "0 2px",
        }}
      >
        {part}
      </mark>
    ) : (
      part
    ),
  );
}

// ─── Section header ───────────────────────────────────────────────────────────
const SectionHeader: React.FC<{ label: string; count: number }> = ({
  label,
  count,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Typography variant="h6" sx={{ color: "white", lineHeight: 1 }}>
      {label}
    </Typography>
    <Box
      sx={{
        background: "rgba(255,255,255,0.25)",
        borderRadius: "12px",
        px: 1,
        py: 0.1,
      }}
    >
      <Typography variant="caption" sx={{ color: "white", fontWeight: 600 }}>
        {count}
      </Typography>
    </Box>
  </Box>
);

// ─── Case card ────────────────────────────────────────────────────────────────
const CaseCard: React.FC<{ caseItem: any; searchTerm: string }> = ({
  caseItem,
  searchTerm,
}) => {
  const companyName =
    caseItem.firmRecord?.["Company Name"] || caseItem.firm || "Unknown";
  const contact = caseItem.firmRecord?.Contact || "";
  const status = caseItem.status || "";
  const dateStr = caseItem.lastActivityDate || caseItem.date || "";
  const displayDate = dateStr ? new Date(dateStr).toLocaleDateString() : "";

  return (
    <Paper
      elevation={1}
      component={Link}
      to={`/case?id=${caseItem._id}`}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        p: 2,
        display: "block",
        textDecoration: "none",
        color: "inherit",
        "&:hover": { boxShadow: 4 },
        borderRadius: 2,
      }}
    >
      <Typography variant="subtitle2" fontWeight={700} noWrap>
        {highlightMatch(companyName, searchTerm)}
      </Typography>
      {contact && (
        <Typography variant="body2" color="text.secondary" noWrap>
          {highlightMatch(contact, searchTerm)}
        </Typography>
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
        {status && (
          <Typography
            variant="caption"
            sx={{
              background: status === "active" ? "#dcfce7" : "#f1f5f9",
              color: status === "active" ? "#166534" : "#475569",
              borderRadius: 1,
              px: 0.75,
              py: 0.25,
              textTransform: "capitalize",
            }}
          >
            {status}
          </Typography>
        )}
        {displayDate && (
          <Typography variant="caption" color="text.secondary">
            {displayDate}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

// ─── Activity card ────────────────────────────────────────────────────────────
const ActivityCard: React.FC<{
  activity: any;
  searchTerm: string;
  onOpen: (activity: any) => void;
}> = ({ activity, searchTerm, onOpen }) => {
  const companyName = activity.firmRecord?.["Company Name"] || "Unknown";
  const activityType = activity.activity_type || "";
  const note = activity.activity_note || "";
  const dateStr = activity.interaction_date || activity.created_at || "";
  const displayDate = dateStr ? new Date(dateStr).toLocaleDateString() : "";
  const notePreview = note.length > 100 ? note.slice(0, 100) + "…" : note;

  return (
    <Paper
      elevation={1}
      onClick={() => onOpen(activity)}
      sx={{
        p: 2,
        cursor: "pointer",
        "&:hover": { boxShadow: 4 },
        borderRadius: 2,
      }}
    >
      <Typography variant="subtitle2" fontWeight={700} noWrap>
        {highlightMatch(companyName, searchTerm)}
      </Typography>
      {activityType && (
        <Typography
          variant="caption"
          sx={{
            background: "#e0f2fe",
            color: "#0369a1",
            borderRadius: 1,
            px: 0.75,
            py: 0.25,
            display: "inline-block",
            mb: 0.5,
          }}
        >
          {activityType}
        </Typography>
      )}
      {notePreview && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {highlightMatch(notePreview, searchTerm)}
        </Typography>
      )}
      {displayDate && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.5 }}
        >
          {displayDate}
        </Typography>
      )}
    </Paper>
  );
};

// ─── Activity popup dialog ────────────────────────────────────────────────────
const ActivityPopup: React.FC<{
  activity: any | null;
  searchTerm: string;
  onClose: () => void;
}> = ({ activity, searchTerm, onClose }) => {
  if (!activity) return null;
  const companyName = activity.firmRecord?.["Company Name"] || "Unknown";
  const contact = activity.firmRecord?.Contact || "";
  const activityType = activity.activity_type || "";
  const note = activity.activity_note || "";
  const dateStr = activity.interaction_date || activity.created_at || "";
  const displayDate = dateStr ? new Date(dateStr).toLocaleDateString() : "";
  const caseId = activity.caseRecord?._id?.toString() || activity.case_id || "";

  return (
    <Dialog open={Boolean(activity)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {companyName}
            </Typography>
            {contact && (
              <Typography variant="body2" color="text.secondary">
                {contact}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 2, flexShrink: 0 }}>
            {caseId && (
              <a
                href={`/case?id=${caseId}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none" }}
              >
                <StyledButtonMain
                  variant="contained"
                  size="small"
                  endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                >
                  Open Case
                </StyledButtonMain>
              </a>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
          {activityType && (
            <Chip
              label={activityType}
              size="small"
              sx={{ background: "#e0f2fe", color: "#0369a1" }}
            />
          )}
          {displayDate && (
            <Chip label={displayDate} size="small" variant="outlined" />
          )}
        </Box>
        {note ? (
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {highlightMatch(note, searchTerm)}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No activity note
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── File card ────────────────────────────────────────────────────────────────
const FileCard: React.FC<{
  file: {
    storageName: string;
    originalName: string;
    case_id: string;
    download_case_id?: string;
    firm_name?: string;
    snippet?: string;
  };
  searchTerm: string;
}> = ({ file, searchTerm }) => {
  // Files stay on disk under the case they were uploaded to, which a case merge
  // never moves — so the download path uses download_case_id, while the links
  // below use case_id to reach the surviving case.
  const downloadUrl = `${process.env.REACT_APP_API_DOMAIN}/upload/case-doc/${
    file.download_case_id || file.case_id
  }/${file.storageName}/${encodeURIComponent(file.originalName)}`;

  const firmDisplay = file.firm_name || "";

  return (
    <Paper
      elevation={1}
      sx={{ p: 2, borderRadius: 2, "&:hover": { boxShadow: 4 } }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
          {firmDisplay && (
            <Link
              to={`/case?id=${file.case_id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                {highlightMatch(firmDisplay, searchTerm)}
              </Typography>
            </Link>
          )}
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}
          >
            <InsertDriveFileOutlinedIcon
              sx={{ fontSize: 14, color: "text.secondary", flexShrink: 0 }}
            />
            <Link
              to={`/case?id=${file.case_id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                textDecoration: "none",
                color: "inherit",
                overflow: "hidden",
              }}
            >
              <Typography variant="body2" color="text.secondary" noWrap>
                {highlightMatch(file.originalName, searchTerm)}
              </Typography>
            </Link>
          </Box>
          {file.snippet && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              {highlightMatch(file.snippet, searchTerm)}
            </Typography>
          )}
        </Box>
        <a
          href={downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton size="small" title="Download file">
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </a>
      </Box>
    </Paper>
  );
};

// ─── Empty state ──────────────────────────────────────────────────────────────
const EmptyState = () => (
  <Typography sx={{ p: 2, color: "text.secondary" }}>No matches</Typography>
);

// ─── Accordion wrapper ────────────────────────────────────────────────────────
const Section: React.FC<{
  label: string;
  count: number;
  defaultExpanded: boolean;
  children: React.ReactNode;
}> = ({ label, count, defaultExpanded, children }) => (
  <Accordion defaultExpanded={defaultExpanded} sx={{ mb: 1 }}>
    <AccordionSummary
      expandIcon={<ExpandMoreIcon sx={{ color: "white" }} />}
      sx={{
        backgroundColor: BRAND,
        borderRadius: "8px 8px 0 0",
        "& .MuiAccordionSummary-content": { my: "6px" },
        "&.Mui-expanded": { borderRadius: "8px 8px 0 0" },
      }}
    >
      <SectionHeader label={label} count={count} />
    </AccordionSummary>
    <AccordionDetails sx={{ p: 2 }}>{children}</AccordionDetails>
  </Accordion>
);

// ─── Main component ───────────────────────────────────────────────────────────
interface EngagementsSearchResultProps {
  searchTerm: string;
  searchResults: {
    case_results: any[];
    activity_results: any[];
    file_results: any[];
  };
}

const EngagementsSearchResult: React.FC<EngagementsSearchResultProps> = ({
  searchTerm,
  searchResults,
}) => {
  const { case_results, activity_results, file_results } = searchResults;
  const [openActivity, setOpenActivity] = useState<any | null>(null);

  return (
    <Box sx={{ mt: 1 }}>
      <ActivityPopup
        activity={openActivity}
        searchTerm={searchTerm}
        onClose={() => setOpenActivity(null)}
      />

      <Section label="Cases" count={case_results.length} defaultExpanded={true}>
        {case_results.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {case_results.map((c: any) => (
              <CaseCard key={c._id} caseItem={c} searchTerm={searchTerm} />
            ))}
          </div>
        )}
      </Section>

      <Section
        label="Activities"
        count={activity_results.length}
        defaultExpanded={true}
      >
        {activity_results.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {activity_results.map((a: any) => (
              <ActivityCard
                key={a._id}
                activity={a}
                searchTerm={searchTerm}
                onOpen={setOpenActivity}
              />
            ))}
          </div>
        )}
      </Section>

      <Section label="Files" count={file_results.length} defaultExpanded={true}>
        {file_results.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {file_results.map((f: any) => (
              <FileCard key={f.storageName} file={f} searchTerm={searchTerm} />
            ))}
          </div>
        )}
      </Section>
    </Box>
  );
};

export default EngagementsSearchResult;
